// ──────────────────────────────────────────────────────────────
// 이번 주말 나들이 — 순수 헬퍼(단일 진실 공급원)
// 주말(토·일) 날짜 계산(KST)·행사 주말겹침 판정·공유문구 생성.
// React state에 의존하지 않아 Node에서 단위검증 가능(회귀 테스트 대상).
// v1(2026-07-26): 화면 + 텍스트 공유. 데이터는 App이 이미 fetch한 state 재사용.
// ──────────────────────────────────────────────────────────────

// 다양한 입력("20260725"/"2026-07-25"/"2026.7.25"/"2026/7/25")을 "YYYY-MM-DD"로.
// 못 파싱하면 '' (상시·빈값·비정형 → 주말겹침 판정에서 제외).
function toYMD(v) {
  if (!v) return '';
  const s = String(v).trim();
  if (!s) return '';
  let m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/(\d{4})[-./](\d{1,2})[-./](\d{1,2})/);
  if (m) return `${m[1]}-${String(m[2]).padStart(2, '0')}-${String(m[3]).padStart(2, '0')}`;
  return '';
}

// 이번 주말(토·일) 계산 — KST 기준. now 주입 가능(테스트/오늘이 주말 아닐 때 검증).
// 정책: 금(5)·토(6)·일(0)에 홈 카드 노출. 금 → 내일·모레 주말, 토 → 오늘·내일, 일 → 어제·오늘.
function weekendDates(now) {
  const base = now instanceof Date ? now : new Date();
  // 실제 순간(절대시각)을 +9h 시프트 후 UTC게터로 읽으면 = KST 벽시계 (로컬 tz 무관)
  const kst = new Date(base.getTime() + 9 * 3600000);
  const y = kst.getUTCFullYear(), mo = kst.getUTCMonth(), d = kst.getUTCDate(), dow = kst.getUTCDay();
  const satOffset = dow === 0 ? -1 : 6 - dow; // 일요일이면 토는 어제
  const mk = (off) => {
    const dt = new Date(Date.UTC(y, mo, d + off));
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
  };
  return { sat: mk(satOffset), sun: mk(satOffset + 1), dow, showCard: dow === 5 || dow === 6 || dow === 0 };
}

// 행사가 이번 주말(토~일 구간)과 겹치는지. ev.date/ev.startDate(시작), ev.endDate(끝, 없으면 단일일).
// 상시(빈 date)는 false — 주말 특정성 우선(빈칸 > 오정보).
function overlapsWeekend(ev, sat, sun) {
  if (!ev || !sat || !sun) return false;
  const start = toYMD(ev.date || ev.startDate);
  if (!start) return false;
  const end = toYMD(ev.endDate) || start;
  const s = start <= end ? start : end;
  const e = start <= end ? end : start;
  return s <= sun && e >= sat; // 구간 겹침 (YYYY-MM-DD는 사전식 비교 = 날짜 비교)
}

// "YYYY.MM.DD ~ YYYY.MM.DD" (단일이면 한 개) — 카드 기간 표시용
function periodStr(sd, ed) {
  const a = toYMD(sd), b = toYMD(ed);
  const fa = a ? a.replace(/-/g, '.') : '';
  const fb = b ? b.replace(/-/g, '.') : '';
  if (fa && fb && fa !== fb) return `${fa} ~ ${fb}`;
  return fa || fb || '';
}

function _md(ymd) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? { mo: +m[2], d: +m[3] } : null;
}

// 공유 문구용 짧은 범위 "7/25~26" (달 다르면 "7/31~8/1")
function weekendRangeShort(sat, sun) {
  const a = _md(sat), b = _md(sun);
  if (!a || !b) return '';
  return a.mo === b.mo ? `${a.mo}/${a.d}~${b.d}` : `${a.mo}/${a.d}~${b.mo}/${b.d}`;
}

// 화면 헤더용 긴 범위 "7월 25일(토)~26일(일)"
function weekendRangeLong(sat, sun) {
  const a = _md(sat), b = _md(sun);
  if (!a || !b) return '';
  return a.mo === b.mo
    ? `${a.mo}월 ${a.d}일(토)~${b.d}일(일)`
    : `${a.mo}월 ${a.d}일(토)~${b.mo}월 ${b.d}일(일)`;
}

// 카톡 공유 메시지 — 부모님 있는 가족방으로 흘러가는 "우리 동네 주말 나들이"
// items: [{title,_period,_place}], region: "서초구", link: 스토어 링크
// 카톡 공유 메시지 — 종류별로 묶고 번호·구분선으로 정리해 "한눈에 읽히는" 형태로 (2026-08-06 사장님 지시).
// 구버전은 제목·기간·장소를 한 줄에 이어붙여 길고 뭉개졌다. 이제 항목마다 2줄(제목 / 📅기간 · 📍장소 · 💰요금)로 분리.
function buildWeekendShareMessage(items, region, link, sat, sun) {
  const list = Array.isArray(items) ? items : [];
  const rg = weekendRangeShort(sat, sun);
  const cut = (s, n) => { const v = String(s || '').trim(); return v.length > n ? v.slice(0, n - 1) + '…' : v; };
  // 종류 분류 — 도서관/공연/전시·축제 (제목·카테고리 기반, 판정 실패는 '나들이')
  const kindOf = (it) => {
    const t = `${it.title || it.name || ''} ${it.category || (it._bd && it._bd.category) || ''}`;
    if (/도서관|독서|그림책|책놀이/.test(t)) return '📚 도서관 프로그램';
    if (/공연|뮤지컬|연극|음악|콘서트|국악|오페라|무용/.test(t)) return '🎭 공연';
    if (/전시|축제|페스티벌|미술|갤러리/.test(t)) return '🎨 축제 · 전시';
    return '🧺 나들이';
  };
  const groups = new Map();
  list.slice(0, 6).forEach((it) => { const k = kindOf(it); if (!groups.has(k)) groups.set(k, []); groups.get(k).push(it); });
  const blocks = [];
  groups.forEach((arr, kind) => {
    const lines = arr.map((it) => {
      const fee = (it._bd && (it._bd.cost || it._bd.fee)) || '';
      const meta = [it._period, it._place, /무료/.test(fee) ? '무료' : fee].filter(Boolean).map((s) => cut(s, 24)).join(' · ');
      return `· ${cut(it.title || it.name, 30)}${meta ? `\n  ${meta}` : ''}`;
    });
    blocks.push(`${kind}\n${lines.join('\n')}`);
  });
  const over = list.length - 6;
  const head = `[이번 주말 나들이]\n${region || '우리 동네'}${rg ? ` · ${rg}` : ''} 가볼 만한 곳 ${list.length}곳 🧺`;
  const tail = `${over > 0 ? `외 ${over}곳 더 있어요\n\n` : ''}일정·요금은 변동될 수 있어요\n상세 내용과 신청은 '가득' 앱에서 👇\n${link}`;
  return `${head}\n\n${blocks.join('\n\n')}\n\n───────────\n${tail}`;
}

module.exports = {
  toYMD,
  weekendDates,
  overlapsWeekend,
  periodStr,
  weekendRangeShort,
  weekendRangeLong,
  buildWeekendShareMessage,
};
